import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Drawer,
	IconButton,
	Stack,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetFilterMetadataQuery } from '../../api/filters.api';
import type { FilterField, FilterMetadata, FilterSection } from '../../api/models/filters.model';
import {
	useCreateSavedSearchMutation,
	useGetCurrentUserQuery,
	useGetSavedSearchesQuery,
} from '../../api/user.api';
import { ACCOUNT_NAME_FILTER } from '../../constants/messages';
import { selectAccount } from '../../redux/slices/accountSlice';
import { showAlert } from '../../redux/slices/alertSlice';
import { clearFilters, setFilters } from '../../redux/slices/filterSlice';
import type { RootState } from '../../redux/store';
import FilterSectionAccordion from './Accordions/FilterSectionAccordion';
import SavedSearchesAccordion from './Accordions/SavedSearchesAccordion';
import ActiveFiltersBar from './ActiveFilters/ActiveFiltersBar';
import './FilterBox.scss';
import FilterBoxBar from './FilterBoxBar';
import FilterDrawerFooter from './FilterDrawerFooter';

// Custom hook to manage pending/active filter state
function usePendingFilters(activeFilters: { [key: string]: any }, drawerOpen: boolean) {
	const [pendingFilters, setPendingFilters] = React.useState<{ [key: string]: any }>(activeFilters);
	React.useEffect(() => {
		if (!drawerOpen) {
			setPendingFilters(activeFilters);
		}
	}, [activeFilters, drawerOpen]);
	return [pendingFilters, setPendingFilters] as const;
}

interface FilterBoxProps {
	sx?: object;
}

const FilterBox: React.FC<FilterBoxProps> = ({ sx }) => {
	const [showRemovedAlert, setShowRemovedAlert] = useState(false);
	const account = useSelector(selectAccount);

	const activeFilters = useSelector((state: RootState) => state.filters.activeFilters);

	// API calls
	const {
		data: filterMetadata = { filterSections: [] as FilterSection[] } as FilterMetadata,
		isLoading: isLoadingMetadata,
		isUninitialized,
	} = useGetFilterMetadataQuery(
		{
			accountId: account.accountId,
			accountTypeCode: account.accountTypeCode,
			sourceId: account.sourceId,
			filters: activeFilters,
		},
		{ skip: !account || !account.accountId }
	);

	const filteredSections = useMemo(() => {
		const sections = Array.isArray(filterMetadata.filterSections)
			? filterMetadata.filterSections
			: [];
		if (account?.accountTypeCode === 'LESSEE') {
			return sections.filter((section) => section.name !== 'account-details');
		}
		return sections;
	}, [filterMetadata.filterSections, account?.accountTypeCode]);

	const dispatch = useDispatch();
	const [drawerOpen, setDrawerOpen] = React.useState(false);
	const [pendingFilters, setPendingFilters] = usePendingFilters(activeFilters, drawerOpen);
	const [findAFilterValue, setFindAFilterValue] = React.useState<string>('');
	const [inputValue, setInputValue] = React.useState('');

	React.useEffect(() => {
		const handler = setTimeout(() => {
			setFindAFilterValue(inputValue);
		}, 100);
		return () => {
			clearTimeout(handler);
		};
	}, [inputValue]);

	// For saved searches
	const { data: user } = useGetCurrentUserQuery();

	const { data: savedSearches } = useGetSavedSearchesQuery(undefined, {
		skip: !user || !user.userId,
	});

	const [createSavedSearch, { isSuccess: isSavedSearchCreated, isError: isSavedSearchError }] =
		useCreateSavedSearchMutation();

	React.useEffect(() => {
		if (isSavedSearchError) {
			dispatch(showAlert({ message: 'Failed to save a search.', type: 'error' }));
		}
		if (isSavedSearchCreated) {
			dispatch(showAlert({ message: 'Search saved successfully.', type: 'success' }));
		}
	}, [isSavedSearchError, isSavedSearchCreated, dispatch]);

	// Open/close drawer
	const toggleDrawer = useCallback(
		(open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
			if (
				event.type === 'keydown' &&
				((event as React.KeyboardEvent).key === 'Tab' ||
					(event as React.KeyboardEvent).key === 'Shift')
			) {
				return;
			}
			setDrawerOpen(open);

			if (!open) {
				setFindAFilterValue('');
			}
		},
		[]
	);

	// Handle field value change in pending filters
	const handleFieldChange = useCallback(
		(field: FilterField, value: any) => {
			setPendingFilters((prev) => {
				let updated;
				if (field.type === 'string' && field.values) {
					if (Array.isArray(value)) {
						// Directly set the array (for ChipFilterField)
						updated = { ...prev, [field.name]: value };
						if (value.length === 0) {
							delete updated[field.name];
						}
					} else {
						// Toggle single value (for other string filter UIs)
						const prevArr = Array.isArray(prev[field.name]) ? prev[field.name] : [];
						let newArr;
						if (prevArr.includes(value)) {
							newArr = prevArr.filter((v: string) => v !== value);
						} else {
							newArr = [...prevArr, value];
						}
						updated = { ...prev, [field.name]: newArr };
						if (newArr.length === 0) {
							delete updated[field.name];
						}
					}
				} else {
					updated = { ...prev, [field.name]: value };
					if (
						value === null ||
						value === undefined ||
						(Array.isArray(value) && value.length === 0)
					) {
						delete updated[field.name];
					}
				}
				return updated;
			});
		},
		[setPendingFilters]
	);

	// Saved Search Functions
	const [open, setOpen] = React.useState(false);
	const [savedSearchName, setSavedSearchName] = React.useState('');

	const handleClickSaveSearch = () => {
		setSavedSearchName('');
		setOpen(true);
	};

	const handleCloseSaveSearchDialog = () => {
		setOpen(false);
	};

	const handleSaveSearch = useCallback(
		(event?: React.FormEvent) => {
			if (event) event.preventDefault();
			const filtersToSave = drawerOpen ? pendingFilters : activeFilters;
			if (!savedSearchName.trim() || !user) return;
			if (savedSearchName.trim())
				createSavedSearch({
					name: savedSearchName.trim(),
					filters: filtersToSave,
				});
			setOpen(false);
		},
		[drawerOpen, pendingFilters, activeFilters, user, savedSearchName, createSavedSearch]
	);

	/**
	 * Checks if a saved search name is already in use.
	 * Case-insensitive, trims whitespace.
	 */
	function isDuplicateSavedSearchName(name: string, savedSearches?: { name: string }[]): boolean {
		const trimmedName = name.trim();
		if (!trimmedName || !Array.isArray(savedSearches)) return false;
		return savedSearches.some(
			(search) => search.name.trim().toLowerCase() === trimmedName.toLowerCase()
		);
	}

	const isDuplicateNameMemo = React.useMemo(() => {
		return isDuplicateSavedSearchName(savedSearchName, savedSearches);
	}, [savedSearchName, savedSearches]);

	const diableModalSaveSearchButton = useCallback(() => {
		if (!savedSearchName) return true;
		if (isDuplicateSavedSearchName(savedSearchName, savedSearches)) return true;
		return false;
	}, [savedSearchName, savedSearches]);
	/** End Save Search */

	useEffect(() => {
		let filters = { ...activeFilters };
		if (filters.hasOwnProperty('sourceName') && account?.accountTypeCode === 'LESSEE') {
			delete filters['sourceName'];
			setShowRemovedAlert(true);
			dispatch(setFilters(filters));
		}
	}, [activeFilters, account, dispatch]);

	// Apply filters: commit pending to active, update Redux, close drawer
	const handleApplyFilters = useCallback(() => {
		dispatch(setFilters(pendingFilters));
		setDrawerOpen(false);
	}, [pendingFilters, dispatch]);

	// Clear all filters
	const handleClearFilters = useCallback(() => {
		setPendingFilters({});
		dispatch(clearFilters());
		setDrawerOpen(false);
	}, [dispatch, setPendingFilters]);

	// Remove a single filter: staged if drawer open, applied if closed
	const handleRemoveFilter = useCallback(
		(field: FilterField, value?: any, rangeType?: 'min' | 'max' | 'range') => {
			const removeFrom = (prev: { [key: string]: any }) => {
				let updated = { ...prev };
				if ((field.type === 'string' || field.type === 'chip') && field.values) {
					const prevArr = Array.isArray(prev[field.name]) ? prev[field.name] : [];
					if (value !== undefined) {
						updated[field.name] = prevArr.filter((v: string) => v !== value);
						if (updated[field.name].length === 0) delete updated[field.name];
					}
				} else if (field.type === 'number' || field.type === 'date') {
					if (rangeType === 'min') {
						delete updated[`${field.name}_min`];
					} else if (rangeType === 'max') {
						delete updated[`${field.name}_max`];
					} else if (rangeType === 'range') {
						delete updated[`${field.name}_min`];
						delete updated[`${field.name}_max`];
					}
				} else if (field.type === 'object') {
					delete updated[field.name];
				}
				return updated;
			};
			if (drawerOpen) {
				setPendingFilters(removeFrom);
			} else {
				const updated = removeFrom(activeFilters);
				dispatch(setFilters(updated));
			}
		},
		[drawerOpen, activeFilters, dispatch, setPendingFilters]
	);

	function areFiltersSelected(): boolean {
		return pendingFilters != null && Object.keys(pendingFilters).length > 0;
	}

	return (
		<Box className="filter-box-container" sx={{ ...sx }} data-testid="active-filters-bar">
			<FilterBoxBar
				onOpenDrawer={toggleDrawer(true)}
				isLoading={isLoadingMetadata || isUninitialized}
			/>
			<ActiveFiltersBar
				filterSections={filteredSections}
				filterValues={activeFilters}
				handleRemoveFilter={handleRemoveFilter}
			/>

			{showRemovedAlert && (
				<Alert
					variant="filled"
					icon={
						<IconButton
							aria-label="close"
							color="inherit"
							size="small"
							onClick={() => setShowRemovedAlert(false)}
							sx={(theme) => ({ p: 0, color: theme.palette.brand.border })}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}
					sx={(theme) => ({
						width: 'fit-content',
						minWidth: 0,
						bgcolor: '#E8F3C9',
						border: `1.5px solid ${theme.palette.brand.border}`,
						color: theme.palette.brand.border,
						'& .MuiAlert-icon': {
							color: theme.palette.brand.border,
						},
					})}
				>
					{ACCOUNT_NAME_FILTER}
				</Alert>
			)}

			<Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
				<Stack>
					<Box
						style={{
							width: 450,
							padding: 16,
							display: 'flex',
							flexDirection: 'column',
							height: '100%',
						}}
					>
						<Box
							sx={{
								pb: 2,
							}}
						>
							<Typography variant="h5" fontWeight="bold" data-testid="filter-drawer-title">
								All Filters
							</Typography>
						</Box>
						<Box
							sx={{
								pb: 2,
							}}
						>
							<Box
								sx={{
									pb: 1,
								}}
							>
								<Typography variant="subtitle1">
									Use the search filters to find specific tasks
								</Typography>
							</Box>
							<Box>
								<TextField
									id="outlined-basic"
									variant="outlined"
									label={
										<Box sx={{ display: 'flex', alignItems: 'center' }}>
											<SearchIcon sx={{ mr: 1 }} />
											Find a filter
										</Box>
									}
									fullWidth={true}
									maxRows={1}
									size="small"
									value={inputValue}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
										setInputValue(event.target?.value)
									}
								/>
							</Box>
						</Box>
						<Box style={{ flex: 1, overflowY: 'auto' }}>
							<SavedSearchesAccordion
								findAFilterValue={findAFilterValue}
								pendingFilterValues={pendingFilters}
								onSelectSavedSearch={setPendingFilters}
							/>
							{filteredSections.map((section) => (
								<FilterSectionAccordion
									key={section.name}
									section={section}
									pendingFilterValues={pendingFilters}
									handleFieldChange={handleFieldChange}
									findAFilterValue={findAFilterValue}
								/>
							))}
						</Box>
						<FilterDrawerFooter
							onApply={handleApplyFilters}
							onClear={handleClearFilters}
							onSave={handleClickSaveSearch}
							filtersSelected={areFiltersSelected()}
						/>
					</Box>
				</Stack>
			</Drawer>
			<Dialog open={open} onClose={handleCloseSaveSearchDialog} maxWidth="sm" fullWidth>
				<DialogTitle>Save Search</DialogTitle>
				<DialogContent>
					<form onSubmit={handleSaveSearch} id="saved-search-form">
						<TextField
							autoFocus
							required
							margin="dense"
							id="name"
							name="saved-search-name"
							label="Name Your Search"
							type="text"
							value={savedSearchName}
							onChange={(e) => setSavedSearchName(e.target.value)}
							error={isDuplicateNameMemo}
							helperText={
								isDuplicateNameMemo ? 'This name is already in use for another saved search.' : ''
							}
							fullWidth
						/>
					</form>
				</DialogContent>
				<DialogActions sx={{ mb: 2 }}>
					<Button onClick={handleCloseSaveSearchDialog}>Cancel</Button>
					<Button
						type="submit"
						form="saved-search-form"
						variant="contained"
						color="primary"
						disabled={diableModalSaveSearchButton()}
						sx={{
							fontSize: 13,
							borderRadius: '20px',
						}}
					>
						Save Search
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default FilterBox;
